import React, { useEffect, useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { CreditNote, Invoice, Quotation } from '../../types';
import { IssuerHeader } from './IssuerHeader';
import { calculateInvoicePreTaxLine, calculateInvoiceTotals, formatCurrency, type InvoiceTotals } from './utils';
export type DocumentPreviewAction = 'view' | 'print' | 'download';
type DocumentCustomer = {
  name: string;
  fileNumber?: string;
  address?: string;
  contact?: string;
};
type BillingDocumentPreviewModalProps = {
  record: Quotation | CreditNote;
  documentType: 'Quotation' | 'Credit Note';
  customer: DocumentCustomer;
  relatedInvoice?: Invoice;
  onClose: () => void;
  initialAction?: DocumentPreviewAction;
};
export function BillingDocumentPreviewModal({
  record,
  documentType,
  customer,
  relatedInvoice,
  onClose,
  initialAction = 'view'
}: BillingDocumentPreviewModalProps) {
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const isQuotation = documentType === 'Quotation';
  const quotation = isQuotation ? record as Quotation : undefined;
  const creditNote = !isQuotation ? record as CreditNote : undefined;
  const documentNumber = isQuotation ? quotation?.quotationNumber ?? '' : creditNote?.creditNoteNumber ?? '';
  const quotationTotals = quotation ? calculateInvoiceTotals(quotation.items) : undefined;
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await captureToA4Pdf(printableRef.current, `BMED_${documentNumber.replace(/[^a-z0-9-_]/gi, '_')}.pdf`);
    } catch (error) {
      console.error('PDF generation failed', error);
      window.alert('Failed to generate the PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  useEffect(() => {
    if (initialAction === 'print') {
      const timer = window.setTimeout(handlePrint, 0);
      return () => window.clearTimeout(timer);
    }
    if (initialAction === 'download') {
      const timer = window.setTimeout(() => void handleDownloadPdf(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, []);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="billing-document-preview-title" data-id="element-984" data-mp="ownup7zximmf">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-985" data-mp="29g137433m71x">
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-986" data-mp="2j62su0b3nxiy">
          <h2 id="billing-document-preview-title" className="text-lg font-semibold text-slate-900" data-id="element-987" data-mp="1vgi759nkam5z">
            {documentType} {documentNumber}
          </h2>
          <div className="flex items-center gap-2" data-id="element-988" data-mp="24iho61yktq8x">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-989" data-mp="1zhbuo1x3dyyc">
              <PrinterIcon className="mr-1 h-4 w-4" data-id="element-990" data-mp="1zk6fhuk0plsa" /> Print
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-991" data-mp="3fu8r85hfhtos">
              <DownloadIcon className="mr-1 h-4 w-4" data-id="element-992" data-mp="a213f0rwic98" />
              {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={`Close ${documentType.toLowerCase()}`} data-id="element-993" data-mp="f1njdhz466lq">
              <XIcon className="h-5 w-5" data-id="element-994" data-mp="szlv0fm1voxj" />
            </button>
          </div>
        </div>

        <article ref={printableRef} className="billing-document bg-white p-0 text-slate-950" data-id="element-995" data-mp="xyyhv0cx8w04">
          <header className="flex flex-col gap-6 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-996" data-mp="12qymh63xsd31">
            <IssuerHeader data-id="element-997" data-mp="17rdvjlh6qyf7" />
            <div className="text-left sm:text-right" data-id="element-998" data-mp="1cp5ydajdc4y6">
              <h1 className="text-2xl font-bold tracking-[0.14em] text-slate-800 sm:text-3xl" data-id="element-999" data-mp="1cmjgoi2rk928">
                {documentType.toUpperCase()}
              </h1>
              <p className="mt-3" data-id="element-1000" data-mp="319bi25ez04cl">
                <span className="font-semibold" data-id="element-1001" data-mp="319las0regplz">{documentType} #:</span>{' '}
                {documentNumber}
              </p>
              <p data-id="element-1002" data-mp="2s7kep77wk9lw">
                <span className="font-semibold" data-id="element-1003" data-mp="2s729hl39il1u">Date:</span>{' '}
                {new Date(record.date).toLocaleDateString()}
              </p>
              {quotation && <p data-id="element-1004" data-mp="3kw9hrbsiqp0a">
                  <span className="font-semibold" data-id="element-1005" data-mp="3kwrba0nsqp4i">Valid until:</span>{' '}
                  {new Date(quotation.validUntil).toLocaleDateString()}
                </p>}
              <p data-id="element-1006" data-mp="3b6g5e8kjy5uz">
                <span className="font-semibold" data-id="element-1007" data-mp="3b5wmaoj61bfd">Status:</span> {record.status}
              </p>
            </div>
          </header>

          <section className="my-7 grid gap-6 border-b border-slate-200 pb-7 sm:grid-cols-2" data-id="element-1008" data-mp="366k2ydri6mhj">
            <div data-id="element-1009" data-mp="7i4s6ofpv722">
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1010" data-mp="7inx94k9q0yo">
                {isQuotation ? 'Prepared For' : 'Credit Issued To'}
              </h2>
              <p className="text-lg font-bold text-slate-900" data-id="element-1011" data-mp="1wzrvuv4m1s36">
                {customer.name}
              </p>
              {customer.fileNumber && <p data-id="element-1012" data-mp="211wn5n7l6cmn">File No: {customer.fileNumber}</p>}
              {customer.address && <p data-id="element-1013" data-mp="1n85mz5fqw53l">{customer.address}</p>}
              {customer.contact && <p data-id="element-1014" data-mp="1s78fjqejgiuo">{customer.contact}</p>}
            </div>
            <div className="sm:text-right" data-id="element-1015" data-mp="1s5nle1w4z1xc">
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1016" data-mp="1da80onhg4idm">
                {isQuotation ? 'Quotation Summary' : 'Related Invoice'}
              </h2>
              {quotation ? <>
                  <p data-id="element-1017" data-mp="14bdzejt673tq">
                    <span className="font-semibold" data-id="element-1018" data-mp="14c5s9mvdungb">Items:</span>{' '}
                    {quotation.items.length}
                  </p>
                  <p data-id="element-1019" data-mp="2ynapo9fvm3n7">
                    <span className="font-semibold" data-id="element-1020" data-mp="2yp5fa123ejxm">Estimate:</span>{' '}
                    {quotationTotals ? formatCurrency(quotationTotals.totalAmount) : '—'}
                  </p>
                </> : <>
                  <p data-id="element-1021" data-mp="38stx7cyi5r7z">
                    <span className="font-semibold" data-id="element-1022" data-mp="38tc3z3isjnu5">Invoice #:</span>{' '}
                    {relatedInvoice?.invoiceNumber ?? '—'}
                  </p>
                  <p data-id="element-1023" data-mp="3hrxgoi1l08n0">
                    <span className="font-semibold" data-id="element-1024" data-mp="3hre8ht1qgym6">Original total:</span>{' '}
                    {relatedInvoice ? formatCurrency(relatedInvoice.totalAmount) : '—'}
                  </p>
                </>}
            </div>
          </section>

          {quotation ? <>
              <div className="overflow-x-auto" data-id="element-1025" data-mp="9ab41hu9jvc7">
                <table className="mb-7 min-w-[720px] w-full border-collapse" data-id="element-1026" data-mp="5c3mwlvw0s0h">
                  <thead data-id="element-1027" data-mp="2b3h2qllqwsyu">
                    <tr className="border-b-2 border-slate-300 bg-slate-100" data-id="element-1028" data-mp="2b520sl79qu1g">
                      <th scope="col" className="p-2.5 text-left text-xs font-bold uppercase text-slate-600" data-id="element-1029" data-mp="272xa9vc4adj1">
                        Service
                      </th>
                      <th scope="col" className="p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1030" data-mp="30klteebr530o">
                        Unit Price
                      </th>
                      <th scope="col" className="p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1031" data-mp="2cor8kuffuzte">
                        Discount
                      </th>
                      <th scope="col" className="p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1032" data-mp="2loe798gs9rir">
                        Pre-tax Total
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1033" data-mp="5wqdtn0zah4p">
                    {quotation.items.map(item => {
                  const line = calculateInvoicePreTaxLine(item);
                  return <tr key={item.id} className="border-b border-slate-200" data-id="element-1034" data-mp="b2lv0leutmm6">
                          <td className="p-2.5" data-id="element-1035" data-mp="6bn996b3jwbj">
                            <p className="font-semibold" data-id="element-1036" data-mp="6ac7td54j0nl">{item.serviceName}</p>
                            <p className="mt-0.5 text-xs text-slate-500" data-id="element-1037" data-mp="29s9454wjozc">
                              Qty {item.quantity}
                              {item.description ? ` · ${item.description}` : ''}
                            </p>
                          </td>
                          <td className="p-2.5 text-right" data-id="element-1038" data-mp="3ji5h90h3y4rq">
                            {formatCurrency(item.unitPrice)}
                          </td>
                          <td className="p-2.5 text-right" data-id="element-1039" data-mp="3fiwn3ui5o4vn">
                            {item.discount}%
                            <span className="block text-xs text-red-600" data-id="element-1040" data-mp="1hp7vfolr0cga">
                              -{formatCurrency(line.discountAmount)}
                            </span>
                          </td>
                          <td className="p-2.5 text-right font-semibold" data-id="element-1041" data-mp="2qqu1jdg6cg3y">
                            {formatCurrency(line.total)}
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
              {quotationTotals && <DocumentTotals totals={quotationTotals} totalLabel="Total" data-id="element-1042" data-mp="27xq4rj7ntn54" />}
              {(quotation.notes || quotation.terms) && <section className="mt-8 grid gap-6 border-t border-slate-200 pt-6 sm:grid-cols-2" data-id="element-1043" data-mp="22xbvxyy8i961">
                  {quotation.notes && <DocumentText label="Notes" value={quotation.notes} data-id="element-1044" data-mp="2cx2q1zivum5l" />}
                  {quotation.terms && <DocumentText label="Terms" value={quotation.terms} data-id="element-1045" data-mp="1p6fff6hl1ifc" />}
                </section>}
            </> : creditNote ? <section className="space-y-6" data-id="element-1046" data-mp="3vv7peavhk8qi">
              <div className="border-y-2 border-slate-300 py-5" data-id="element-1047" data-mp="3vu61v6xa9trw">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-1048" data-mp="4id2q7vv6r3x">
                  <span className="text-base font-semibold" data-id="element-1049" data-mp="3mwvnhcp65oir">Credit amount</span>
                  <span className="text-2xl font-bold text-emerald-700" data-id="element-1050" data-mp="3qwed2lrxqnrx">
                    {formatCurrency(creditNote.amount)}
                  </span>
                </div>
              </div>
              <DocumentText label="Reason for credit" value={creditNote.reason} data-id="element-1051" data-mp="ngpb3reybwyg" />
              {relatedInvoice && <div className="rounded-md border border-slate-200 bg-slate-50 p-4" data-id="element-1052" data-mp="9j0tqtk3f7y5">
                  <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1053" data-mp="ehvhm9wx3mp4">
                    Invoice financial summary
                  </h2>
                  <div className="grid gap-3 text-sm sm:grid-cols-3" data-id="element-1054" data-mp="2z6rndfb6w6wi">
                    <p data-id="element-1055" data-mp="14qb1x3zz78pf">
                      <span className="block text-slate-500" data-id="element-1056" data-mp="14oggzpopfglc">
                        Invoice total
                      </span>
                      <span className="font-semibold" data-id="element-1057" data-mp="zsrqebunxa16">
                        {formatCurrency(relatedInvoice.totalAmount)}
                      </span>
                    </p>
                    <p data-id="element-1058" data-mp="qxlh32w6o9tw">
                      <span className="block text-slate-500" data-id="element-1059" data-mp="qwaeh6eh584d">Paid to date</span>
                      <span className="font-semibold" data-id="element-1060" data-mp="m0v08dck1a7f">
                        {formatCurrency(relatedInvoice.amountPaid)}
                      </span>
                    </p>
                    <p data-id="element-1061" data-mp="c25hyjgqxnqn">
                      <span className="block text-slate-500" data-id="element-1062" data-mp="c1m8zs92q9or">Balance due</span>
                      <span className="font-semibold" data-id="element-1063" data-mp="80k8z97c1tlq">
                        {formatCurrency(relatedInvoice.balanceDue)}
                      </span>
                    </p>
                  </div>
                </div>}
            </section> : null}

          <footer className="mt-12 border-t border-slate-200 pt-5 text-xs text-slate-500" data-id="element-1064" data-mp="3kbolaor8as9t">
            This document was generated by BMED Medical Centre billing.
          </footer>
        </article>
      </div>
    </div>;
}
type DocumentTotalsProps = {
  totals: InvoiceTotals;
  totalLabel: string;
};
function DocumentTotals({
  totals,
  totalLabel
}: DocumentTotalsProps) {
  return <dl className="ml-auto w-full max-w-xs" data-id="element-1065" data-mp="31cao7xy7d1f3">
      <div className="flex justify-between py-1" data-id="element-1066" data-mp="31dm1codjfliv">
        <dt className="text-slate-600" data-id="element-1067" data-mp="36bduflpx5don">Subtotal:</dt>
        <dd className="font-medium" data-id="element-1068" data-mp="36c6u62qgn2xa">{formatCurrency(totals.subtotal)}</dd>
      </div>
      {totals.totalDiscount > 0 && <div className="flex justify-between py-1" data-id="element-1069" data-mp="3af3epyzk993v">
          <dt className="text-slate-600" data-id="element-1070" data-mp="3fcbxa0omff5n">Discount:</dt>
          <dd className="font-medium text-red-600" data-id="element-1071" data-mp="3f9ph5cnnzrbt">
            -{formatCurrency(totals.totalDiscount)}
          </dd>
        </div>}
      <div className="flex justify-between border-b border-slate-300 py-1" data-id="element-1072" data-mp="ul1rs94q4ukd">
        <dt className="text-slate-600" data-id="element-1073" data-mp="t4fk5gwzu41h">TOT Tax (5%):</dt>
        <dd className="font-medium" data-id="element-1074" data-mp="t57d0jz7hno2">{formatCurrency(totals.totalTax)}</dd>
      </div>
      <div className="flex justify-between py-3 text-lg font-bold" data-id="element-1075" data-mp="o6w4qdok7l7y">
        <dt data-id="element-1076" data-mp="125vk0uq6mnrg">{totalLabel}:</dt>
        <dd className="text-blue-700" data-id="element-1077" data-mp="12659mh7beswm">{formatCurrency(totals.totalAmount)}</dd>
      </div>
    </dl>;
}
type DocumentTextProps = {
  label: string;
  value: string;
};
function DocumentText({
  label,
  value
}: DocumentTextProps) {
  return <div data-id="element-1078" data-mp="1c170pil5i51u">
      <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1079" data-mp="174gjgrxm13eu">
        {label}
      </h2>
      <p className="whitespace-pre-wrap text-slate-700" data-id="element-1080" data-mp="1lt37rj57t5i7">{value}</p>
    </div>;
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}